import type { BookingStore } from './schema/BookingStore';
import { createStore } from 'owa-satcheljs';
import {
    type RequestState,
    BookingPageErrorState,
    DEFAULT_ENTRYPOINT,
    DEFAULT_PALETTEID,
} from 'bookwithme-shared';
import { AVAILABILITY_DATA_FETCH_RANGE_IN_MONTHS } from '../utils/bookingAvailabilityUtils';
import { defaultBanner } from '../utils/defaultBanner';
import { TimeSuggestionsRequestState } from './schema/TimeSuggestionsRequestState';
import { BookMeetingStatus } from '../services/schema/BookMeetingResponse';
import { today, addMonths, now } from 'owa-datetime';
import type { BookableTimeSlot } from './schema/BookableTimeSlot';

const initialBookingStore: BookingStore = {
    // Booking mailbox
    bookingData: {
        Booking: {
            Id: '',
            AnsweredQuestionList: [],
            CustomerEmail: '',
            CustomerName: '',
            CustomerNotes: undefined,
            CustomerTimeZone: '',
            FormattedDuration: '',
            Location: '',
            ServiceId: '',
            ServiceName: '',
            StaffList: '',
            Start: '',
            StartInCustomerTimeZone: '',
        },
        BookingMailboxAddress: null,
        BookingMailboxHash: null,
        BookingMailboxPuid: null,
        BusinessInfo: {
            Name: '',
        },
        BusinessLogo: null,
        BusinessTimeZone: 'UTC',
        BusinessTimeZoneName: 'UTC',
        StaffMembers: [],
        Services: [],
        Settings: {
            PublishingUrl: '',
            ServiceRequestUrl: '',
            ThemeData: { PaletteId: DEFAULT_PALETTEID },
            IsLogoHidden: false,
            IsConsentNeeded: false,
            ConsentText: '',
            CustomColorScheme: '',
            AuthType: 0,
            IsNewC2RevampedEnabled: false,
            PageOrigin: '',
        },
        IsReadRequest: false,
        LoggedInUserHashedEmail: null,
        UserClientId: null,
    },
    bookingMailbox: '',
    obscuredBookingEmailAddress: '',
    isPrivate: false,
    bookingSignedUrl: '',
    isSingleUseLink: false,

    // Availability, timeslots, and time suggestions
    bookingAvailability: {
        StaffBookabilities: [],
        TimeZone: 'UTC',
        DateTimeNowInTimeZone: '',
    },
    displayTimeZone: 'UTC',
    bookableTimeSlots: new Map<number, BookableTimeSlot | null>(),
    showTimeSuggestions: false,
    timeSuggestions: [],
    /* eslint-disable-next-line owa-custom-rules/no-side-effects -- (https://aka.ms/OWALintWiki)
     * DO NOT COPY-PASTE!, this function can't be called in the module scope, please move it inside a function
     *	> Function now called at module scope is not allowed. This may cause unwanted side effects. */
    earliestTimeAvailableToBook: now(),
    /* eslint-disable-next-line owa-custom-rules/no-side-effects -- (https://aka.ms/OWALintWiki)
     * DO NOT COPY-PASTE!, this function can't be called in the module scope, please move it inside a function
     *	> Function addMonths called at module scope is not allowed. This may cause unwanted side effects.
     *	> Function today called at module scope is not allowed. This may cause unwanted side effects. */
    maxDateAllowedForBooking: addMonths(today(), AVAILABILITY_DATA_FETCH_RANGE_IN_MONTHS),
    fetchedMonths: [],
    timeSuggestionFilters: {
        isAvailable: false,
        isMeetingHours: false,
    },

    // Data needed for booking
    customerInfo: {
        Name: '',
        Email: '',
        Notes: undefined,
    },
    customerInfoNeeded: {
        nameNeeded: false,
        emailNeeded: false,
    },
    emailVerificationNeeded: true,
    emailVerificationCode: '',
    /* eslint-disable-next-line owa-custom-rules/no-side-effects -- (https://aka.ms/OWALintWiki)
     * DO NOT COPY-PASTE!, this function can't be called in the module scope, please move it inside a function
     *	> Function today called at module scope is not allowed. This may cause unwanted side effects. */
    selectedDate: today(),
    selectedMeeting: {
        MeetingTypeId: null,
        Start: null,
        End: null,
        Origin: null,
    },
    restrictedDates: [],
    weekStartDay: 0,
    timeSuggestionMap: new Map<string, BookableTimeSlot[]>(),
    selectedDateTimeSuggestions: [],
    showBookMeetingModal: {
        showBookMeetingModal: false,
        showCustomerInfoModal: false,
        showProgressModal: false,
        showVerificationModal: false,
        showBookingConfirmationModal: false,
        showBookingErrorModal: false,
    },
    bookMeetingStatus: BookMeetingStatus.NotInitiated,

    // Authentication & accounts
    accountSelectedOrGuest: false,
    alternateAccounts: [],
    isAccountSelected: false,
    isAuthRequiredToAccessBookingPage: false,
    isEmailVerificationSelected: false,
    tempAllowedAccounts: [],
    attendeeHasAccessThroughGroupOrDL: false,

    // Request states
    initialRequestState: 0,
    timeSuggestionsRequestState: TimeSuggestionsRequestState.NoSuggestionsFound,
    bookingPageErrorState: BookingPageErrorState.None,
    editBooking: true,
    urlBookingId: '',
    currentBookingService: null,
    /* eslint-disable-next-line owa-custom-rules/no-side-effects -- (https://aka.ms/OWALintWiki)
     * DO NOT COPY-PASTE!, this function can't be called in the module scope, please move it inside a function
     *	> Function defaultBanner called at module scope is not allowed. This may cause unwanted side effects. */
    themeImage: defaultBanner(),
    currentMeetingTypeId: '',
    entryPoint: DEFAULT_ENTRYPOINT,
    wasPrimed: false,

    // Edit state for secondary attendees in booking
    additionalAttendees: [],
    bookingMailboxSmtpAddress: '',

    //custom questions
    customQuestions: [],

    xBEServer: '',
};

/* eslint-disable-next-line owa-custom-rules/no-side-effects -- (https://aka.ms/OWALintWiki)
 * DO NOT COPY-PASTE!, this function can't be called in the module scope, please move it inside a function
 *	> Function createStore (IIFE) called at module scope is not allowed. This may cause unwanted side effects. */
const store = createStore<BookingStore>('bookwithme-attendee', initialBookingStore)();
export default store;
